import React, { Suspense, useEffect, useRef, useState } from 'react';
import FinalMolGrid from '../../components/FinalMolGrid/FinalMolGrid';
import { AdmissionCommitteeDataListConfig } from './data/AdmissionCommitteeDataList.Data';
import DataLoader from '../../components/FinalMolGrid/ui/dataLoader/DataLoader';
import Buttons from '../../components/ui/button/Buttons';
import {
  useAddEditManageAdmissionMemberMutation,
  useDeleteAdmissionCommitteeMemberByIdMutation,
  useLazyGetAllManageAdmissionMemberListQuery
} from '../../app/services/AdmissionBoardApproval/AdmissionBoardServiceApi';
import { paginationObj } from '../../utils/Constants';
import SwalAlert from '../../services/swalService/SwalService';
import CenterModel from '../../components/ui/centerModel/CenterModel';
import AddAdminMember from './component/AddMemberForm';
import ToastService from '../../services/toastService/ToastService';
import { useNavigate } from "react-router-dom";
import EditMemberForm from './component/EditMemberForm';
import { encryptAESID } from '../../utils/Encryption';
import { getAuditContext } from '../../lib/authenticationLibrary';

const AdmissionCommitteeDataListData = () => {
  /* Refs */
  const AdmissionCommitteeListRef = useRef();
  /* States */
  const [dataSource, setDataSource] = useState(); // Table data source
  const { confirm, success, error } = SwalAlert();
  const audit = getAuditContext();
  const [pagination, setPagination] = useState({ ...paginationObj, });
  const addMemberFormRef = useRef();
  const [openAddModalPopup, setOpenAddModalPopup] = useState(false); // Modal visibility state
  const navigate = useNavigate();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState();

  /* Destructure SwalAlert functions */

  /* API Hooks */
  const [getAllManageAdmissionMemberList, { isLoading: listLoading, isSuccess: listSuccess, data: allMemberResponse }] = useLazyGetAllManageAdmissionMemberListQuery();
  const [addEditManageAdmissionMember, { isLoading: isAddMemberLoading, isSuccess: isAddMemberSuccess, data: addMemberResponse }] = useAddEditManageAdmissionMemberMutation();
  const [deleteAdmissionCommitteeMemberById, { isLoading: isDeleteMemberLoading, isSuccess: isDeleteMemberSuccess, data: deleteMemberResponse }] = useDeleteAdmissionCommitteeMemberByIdMutation();

  /* Fetch Data and Update State */
  useEffect(() => {
    if (!listLoading && listSuccess && allMemberResponse) {
      setDataSource(allMemberResponse.itemList);
      setPagination((prev) => ({ ...prev, totalCount: allMemberResponse.totalCount }));
    }
  }, [listLoading, listSuccess, allMemberResponse]);

  /* Handle Add/Edit Member Response */
  useEffect(() => {
    if (!isAddMemberLoading && isAddMemberSuccess && addMemberResponse) {
      if (!addMemberResponse?.hasError) {
        success("Record has been added successfully.");
        setOpenAddModalPopup(false);
        getAllManageAdmissionMemberList(pagination);
      } else {
        error(addMemberResponse?.errorMessage);
      }
    }
  }, [isAddMemberLoading, isAddMemberSuccess, addMemberResponse]);

  /* Handle Delete Member Response */
  useEffect(() => {
    if (!isDeleteMemberLoading && isDeleteMemberSuccess && deleteMemberResponse) {
      if (!deleteMemberResponse.hasError) {
        success("Record Deleted Successfully.");
        getAllManageAdmissionMemberList(pagination);
      } else {
        error(deleteMemberResponse.errorMessage);
      }
    }
  }, [isDeleteMemberLoading, isDeleteMemberSuccess, deleteMemberResponse]);

  /* Initial Data Fetch */
  useEffect(() => {
    getAllManageAdmissionMemberList(pagination);
  }, []);

  /* Pagination Handler */
  const handlePageChange = (page) => {
    const { pageNumber, pageSize } = page;
    getAllManageAdmissionMemberList({ ...pagination, pageSize, pageNo: pageNumber });
  };


  /* Add Member Modal Toggle */
  const handleAddMember = () => {
    setOpenAddModalPopup(!openAddModalPopup);
  };

  /* Edit Member Handler */
  // const handleEditClick = (data) => {
  //   const request = {
  //     AdminID: data.adminID,
  //     Name: data.name,
  //     Email: data.email
  //   };
  //   addEditManageAdmissionMember(request);
  // };

  const handleEditClick = (data) => {
    setSelectedId(data.adminID)
    setIsEditModalOpen(!isEditModalOpen)
  }

  /* Delete Member Handler */
  const handleDeleteClick = (data) => {
    confirm('Delete?', 'Are you sure you want to Delete?', 'Delete', 'Cancel').then((confirmed) => {
      if (confirmed) {
        const request = {
          id: data.adminID,
          id2: audit.adminUserId,
          id3: audit.appId
        };
        deleteAdmissionCommitteeMemberById(request);
      }
    });
  };

  /* Save New Member Handler */
  const handleSaveMember = () => {
    const formData = addMemberFormRef.current?.getFormData();
    if (!formData) return;

    if (formData.password === formData.confirmPassword) {
      const request = {
        Name: formData.name,
        Email: formData.emailAddress,
        Password: formData.password,
        CreatedBy: audit.adminUserId,
        CreatedAppId: audit.appId
      };
      addEditManageAdmissionMember(request);
    } else {
      ToastService.error('Password and Confirm Password Not Match');
    }
  };

  /* Grid Row Update Handler */
  const onRowDataUpdate = (updateData) => {
    handleEditClick(updateData);
  };

  /* Action Handlers */
  const actionHandler = {
    EDIT: handleEditClick,
    DELETE: handleDeleteClick
  };
  const editOpenModel = () => {
    setIsEditModalOpen(!isEditModalOpen);
    getAllManageAdmissionMemberList(pagination);
  }

  return (
    <div>
      <div className="membership-application">
        <div className="form-card">
          {/* Add Member Button */}
          <Suspense fallback={<DataLoader />}>
            <div className="d-flex justify-content-between ">
              <h5 className="align-self-end mb-1">Total Count: {allMemberResponse?.totalCount || 0}</h5>
              <div className="mb-2">
                <Buttons
                  onClick={handleAddMember}
                  buttonText="Add Member"
                  buttonTypeClassName="theme-btn btn mt-auto"
                />
              </div>
            </div>
          </Suspense>
          {/* Data Table */}
          <div className="table-100">
            <FinalMolGrid
              ref={AdmissionCommitteeListRef}
              configuration={AdmissionCommitteeDataListConfig}
              dataSource={dataSource}
              isLoading={listLoading}
              allowPagination
              pagination={pagination}
              onActionChange={actionHandler}
              onPageChange={handlePageChange}
              allowAddGridItem
              onRowDataUpdate={onRowDataUpdate}
            />
          </div>
          {isEditModalOpen &&
            <CenterModel
              modelTitle={'Edit member'}
              show={"test"}
              handleClose={editOpenModel}
              onUpload={editOpenModel}
            >
              <EditMemberForm selectedId={encryptAESID(selectedId)} />

            </CenterModel>
          }

          {/* Back Button */}
          <Buttons
            buttonText="Back"
            buttonTypeClassName="theme-btn btn mt-2"
          // onClick={searchClick}
          />
        </div>
      </div>

      {/* Add Member Modal */}
      {openAddModalPopup && (
        <CenterModel
          modelTitle="Add Member"
          show
          isLoading={isAddMemberLoading}
          isDisable={isAddMemberLoading}
          handleClose={handleAddMember}
        >
          <AddAdminMember
            ref={addMemberFormRef}
            handleSaveMember={handleSaveMember}
            isLoading={isAddMemberLoading}
          />
        </CenterModel>
      )}
    </div>
  );
};

export default AdmissionCommitteeDataListData;
